/**
 * RoleGapStrip (DC-1612 — FAIR-MM Phase 1)
 *
 * The producer↔consumer perception gap, rendered as a compact strip directly
 * beneath the Perceived leg row inside a PillarCard — a refinement of the
 * Perceived leg, never a fourth leg (spec: v3.0-org-lane-surfaces-ux-spec.md
 * §3). The finding travels in words, never color alone; the attribution
 * basis (contextual interview tags vs. roles derived from personas) is
 * stated because the derived path carries lower confidence by design.
 *
 * States:
 *   - gap present  → scores + directional phrase + attribution + ⓘ disclosure
 *   - indeterminate → phrase only (printing one side's score would invite a
 *     false comparison)
 *   - no role data (attribution null) or breakdown absent → renders nothing
 *
 * The ⓘ disclosure (title attribute — the leg rows' existing affordance
 * level) carries the full three-role breakdown incl. Enabler, and the CC BY
 * 4.0 Pistoia FAIR-MM role attribution (sequencing-plan §3: any surface
 * rendering vendored FAIR-MM vocabulary cites it).
 */

import React from 'react';
import { Info } from 'lucide-react';
import type { LegReading, RolePerceptionReading } from '../../types/triangulation';

interface RoleGapStripProps {
  reading: RolePerceptionReading | undefined;
}

const ATTRIBUTION_LABEL: Record<NonNullable<RolePerceptionReading['attribution']>, string> = {
  contextual: 'attribution: interview context',
  derived: 'attribution: persona roles (derived)',
  mixed: 'attribution: interview context + persona roles',
};

function roleScoreText(label: string, leg: LegReading): string {
  return leg.state === 'present_and_scored' && leg.score !== null
    ? `${label} ${leg.score.toFixed(1)}`
    : `${label} —`;
}

function stateSentence(reading: RolePerceptionReading): string {
  if (reading.gap === null) return 'one side has too little signal to compare';
  const magnitude = Math.abs(reading.gap).toFixed(1);
  switch (reading.gapState) {
    case 'aligned':
      return 'producer and consumer reads agree';
    case 'producer_optimistic':
      return `producers read this pillar higher (+${magnitude})`;
    case 'consumer_optimistic':
      return `consumers read this pillar higher (−${magnitude})`;
    default:
      return 'one side has too little signal to compare';
  }
}

export const RoleGapStrip: React.FC<RoleGapStripProps> = ({ reading }) => {
  // No breakdown requested/computed, or nothing attributed to any role:
  // the strip is absent entirely — absence must not advertise the feature.
  if (!reading || reading.attribution === null) return null;

  const disclosure = [
    roleScoreText('Producer', reading.producer),
    roleScoreText('Consumer', reading.consumer),
    roleScoreText('Enabler', reading.enabler),
    ATTRIBUTION_LABEL[reading.attribution],
    'Roles: Pistoia FAIR Maturity Matrix (CC BY 4.0)',
  ].join(' · ');

  if (reading.gap === null) {
    return (
      <div className="tri-role-gap is-indeterminate" data-testid="role-gap-strip" title={disclosure}>
        <span className="tri-role-gap-state">
          Producer↔consumer read: one side has too little signal to compare.
        </span>
      </div>
    );
  }

  const stateClass =
    reading.gapState === 'aligned' ? 'is-aligned' : 'is-divergent';

  return (
    <div className={`tri-role-gap ${stateClass}`} data-testid="role-gap-strip" title={disclosure}>
      <span className="tri-role-gap-scores">
        Producers <b>{reading.producer.score?.toFixed(1)}</b> · Consumers{' '}
        <b>{reading.consumer.score?.toFixed(1)}</b>
      </span>
      <span className="tri-role-gap-state" data-gap-state={reading.gapState}>
        {stateSentence(reading)}
      </span>
      <span className="tri-role-gap-attrib">
        {ATTRIBUTION_LABEL[reading.attribution]}
        <Info size={12} aria-hidden="true" />
      </span>
    </div>
  );
};
